/**
 * What each settings area currently says about itself.
 *
 * The settings overview answers one question an administrator actually arrives
 * with — is my school set up? — so every area reports its real contents ("3
 * tuition plans, $8,400-$11,200") rather than a status pill. Areas that are
 * genuinely incomplete raise `attention`; areas that are merely optional and
 * empty do not, because marking everything marks nothing.
 */
import type { ResultOf } from 'gql.tada';
import { SETTINGS_SECTIONS } from './settingsSections';
import { openingPricesYear } from './pricesYear';
import type { SchoolSettingsQuery } from './useSchoolSettingsQuery';

type SettingsData = NonNullable<NonNullable<ResultOf<typeof SchoolSettingsQuery>>['school']>;

export interface SettingsSummaryRow {
  slug: string;
  label: string;
  /** The area's contents, in plain language. */
  summary: string;
  /** True only where something has to be set before the area works. */
  attention: boolean;
}

function money(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** The year an administrator is working in, falling back to the newest one. */
function workingYear(school: SettingsData, selectedYearId?: string) {
  const years = school.schoolYears ?? [];
  return years.find((year) => year.id === selectedYearId) ?? years[years.length - 1] ?? null;
}

function schoolYearsRow(school: SettingsData, selectedYearId?: string) {
  const years = school.schoolYears ?? [];
  if (years.length === 0) {
    return { summary: 'No school years yet', attention: true };
  }

  const year = workingYear(school, selectedYearId);
  const terms = year?.termsCount ?? 0;
  if (year && terms === 0) {
    return { summary: `${year.name} has no terms yet`, attention: true };
  }

  return {
    summary: `${count(years.length, 'year')} · ${year?.name} has ${count(terms, 'term')}`,
    attention: false,
  };
}

function gradeLevelsRow(school: SettingsData) {
  const items = [...(school.gradeLevelsSetting?.items ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (items.length === 0) {
    return { summary: 'No grade levels yet', attention: true };
  }

  const first = items[0]?.description;
  const last = items[items.length - 1]?.description;
  const range = items.length > 1 && first && last ? `, ${first} to ${last}` : '';
  return { summary: `${count(items.length, 'level')}${range}`, attention: false };
}

function classroomsRow(school: SettingsData) {
  const rooms = school.classrooms ?? [];
  // Rooms are optional: a school can run its whole schedule without naming any.
  return {
    summary: rooms.length === 0 ? 'None added' : count(rooms.length, 'room'),
    attention: false,
  };
}

function gradeScaleRow(school: SettingsData) {
  const entries = [...(school.gradeScaleSetting?.entries ?? [])]
    .sort((a, b) => b.minimumPercentage - a.minimumPercentage);
  if (entries.length === 0) {
    return { summary: 'No grade scale yet', attention: true };
  }

  const highest = entries[0]?.letter;
  const lowest = entries[entries.length - 1]?.letter;
  const range = entries.length > 1 && highest && lowest ? `, ${highest} to ${lowest}` : '';
  return { summary: `${count(entries.length, 'letter')}${range}`, attention: false };
}

function gradingTemplatesRow(school: SettingsData) {
  const templates = school.gradingTemplates ?? [];
  if (templates.length === 0) {
    return { summary: 'None added', attention: false };
  }

  // Weights that miss 100% still work, but almost never on purpose.
  const offBalance = templates.filter((template) => Math.round(template.weightTotal) !== 100);
  if (offBalance.length > 0) {
    return {
      summary: `${count(offBalance.length, 'template')} not weighted to 100%`,
      attention: true,
    };
  }

  return { summary: count(templates.length, 'template'), attention: false };
}

/** The prices of the year Tuition & fees opens on: the current year, else the first listed. */
function tuitionRow(school: SettingsData) {
  const year = openingPricesYear(school.schoolYears ?? []);
  const plans = year?.tuitionOptionsSetting?.items ?? [];
  const fee = year?.enrollmentFeeSetting?.amountCents ?? 0;
  const feeNote = fee > 0 ? ` · ${money(fee)} fee` : '';

  if (plans.length === 0) {
    return { summary: 'No tuition plans yet', attention: true };
  }

  const costs = plans.map((plan) => plan.costCents);
  const low = Math.min(...costs);
  const high = Math.max(...costs);
  const range = low === high ? money(low) : `${money(low)}\u2013${money(high)}`;
  return { summary: `${count(plans.length, 'plan')}, ${range}${feeNote}`, attention: false };
}

function paymentsRow(school: SettingsData) {
  const account = school.stripeAccount;
  if (!account?.connected) {
    return { summary: 'Stripe not connected', attention: true };
  }
  if (!account.chargesEnabled) {
    return { summary: 'Connected, but not yet able to take payments', attention: true };
  }
  if (!account.payoutsEnabled) {
    return { summary: 'Taking payments, payouts still pending', attention: true };
  }
  return { summary: 'Connected, taking payments and paying out', attention: false };
}

const PARENT_ALERT_KEYS = [
  'invoiceOverdue',
  'invoiceDue',
  'enrollmentAction',
  'missingWork',
  'lowGrade',
] as const;

function parentAlertsRow(school: SettingsData) {
  const setting = school.parentAlertsSetting;
  if (!setting) return { summary: 'Not configured', attention: false };

  const on = PARENT_ALERT_KEYS.filter((key) => setting[key]).length;
  const alerts = on === 0 ? 'Nothing raised to families' : `${on} of ${PARENT_ALERT_KEYS.length} alerts on`;
  // Whether families are let in and whether they hear about invoices are the
  // two things an admin scanning this list needs, before the alert count.
  const portal = setting.enabled ? 'Families can sign in' : 'Families cannot sign in yet';
  const emails = setting.enabled && setting.invoiceEmails ? 'invoice emails on' : 'invoice emails off';
  return { summary: `${portal} · ${emails} · ${alerts}`, attention: false };
}

export function settingsSummary(
  school: SettingsData | null | undefined,
  options: { selectedYearId?: string; userEmail?: string | null } = {}
): SettingsSummaryRow[] {
  if (!school) return [];

  const rows: Record<string, { summary: string; attention: boolean }> = {
    'school-years': schoolYearsRow(school, options.selectedYearId),
    'grade-levels': gradeLevelsRow(school),
    classrooms: classroomsRow(school),
    'grade-scale': gradeScaleRow(school),
    'grading-templates': gradingTemplatesRow(school),
    'tuition-and-fees': tuitionRow(school),
    payments: paymentsRow(school),
    'parent-alerts': parentAlertsRow(school),
    account: { summary: options.userEmail ?? 'Signed in', attention: false },
  };

  return SETTINGS_SECTIONS.map((section) => ({
    slug: section.slug,
    label: section.label,
    ...(rows[section.slug] ?? { summary: '', attention: false }),
  }));
}

export function attentionSlugs(rows: readonly SettingsSummaryRow[]): Set<string> {
  return new Set(rows.filter((row) => row.attention).map((row) => row.slug));
}
